const localGetters = {
  isCommissioner: (s, g, r) => isCommissioner(s, g, r),
  isCurrentlyDrafting: (s, g, r) => isCurrentlyDrafting(s, g, r),
  isDraftComplete: (s, g, r) => isDraftComplete(s, g, r),
  picks: (s, g, r) => picks(s, g, r),
  availablePlayers: (s, g, r) => availablePlayers(s, g, r),
  myTeam: (s, g, r) => myTeam(s, g, r),
  currentRound: (s, g, r) => currentRound(s, g, r),
  roundFromPick: (s, g, r) => roundFromPick(s, g, r),
  teamPicks: (s, g, r, rg) => teamPicks(s, g, r, rg),
  roster: (s, g, r) => roster(s, g, r),
  draftingTeam: (s, g, r) => draftingTeam(s, g, r),
  draftRosters: (s, g, r) => draftRosters(s, g, r),
  lastTeamToPick: (s, g, r) => lastTeamToPick(s, g, r),
  lastDraftedPlayer: (s, g, r, rg) => lastDraftedPlayer(s, g, r, rg),
  mostRecentlyDraftedPlayer: (s, g, r, rg) => mostRecentlyDraftedPlayer(s, g, r, rg),
  roundPickDisplay: (s, g, r) => roundPickDisplay(s, g, r),
  previousPickForDraftingTeam: (s, g, r) => previousPickForDraftingTeam(s, g, r),
  picksAwayForDraftingTeam: (s, g, r) => picksAwayForDraftingTeam(s, g, r),
  draftablePositions: (s, g, r) => draftablePositions(s, g, r),
  keepersByTeam: (s, g, r, rg) => keepersByTeam(s, g, r, rg),
  tradeKeeperRoundOptions: (s, g, r, rg) => tradeKeeperRoundOptions(s, g, r, rg),
};

const isCommissioner = ({ draft }, getters, { auth }) => {
  return draft && draft.commissioner === auth.user.uid;
};

const isCurrentlyDrafting = ({ draft }, getters, { auth }) => {
  if (!draft || !draft.picks || !draft.picks[draft.currentPick - 1]) {
    return false;
  }

  const teamId = draft.picks[draft.currentPick - 1].t;
  const teamUid = draft.teams.find(({ id }) => id === teamId).uid;

  return teamUid === auth.user.uid;
};

const isDraftComplete = ({ draft }) => {
  return draft && draft.picks && draft.currentPick > draft.picks.length;
};

const picks = ({ draft }, getters, { lists: { players } }) => {
  if (!draft || !draft.picks) {
    return [];
  }

  return draft.picks.map(({ t, p, k }) => {
    const team = draft.teams.find(({ id }) => id === t);

    return {
      team: team ? team.name : null,
      player: players[p],
      keeper: k,
    };
  });
};

const availablePlayers = ({ draft }, getters, { lists: { players } }) => {
  if (!draft || !draft.picks) {
    return [];
  }

  return draft.availablePlayers
    .map((playerId, i) => {
      if (!players[playerId]) {
        return {};
      }

      const { name, position, team } = players[playerId];

      return {
        id: playerId,
        name,
        position,
        team,
        rank: i + 1,
      };
    })
    .filter(({ id }) => {
      return id && draft.picks.findIndex(pick => pick.p === id) === -1;
    });
};

const myTeam = ({ draft }, getters, { auth }) => {
  if (!draft) {
    return null;
  }

  return draft.teams.find((team) => {
    return team.uid === auth.user.uid;
  });
};

const currentRound = ({ draft }, getters) => {
  if (!draft) {
    return 0;
  }

  return getters.roundFromPick(draft.currentPick);
};

const roundFromPick = ({ draft }) => (pick) => {
  return Math.ceil(pick / draft.teams.length);
};

const teamPicks = ({ draft }, getters, { lists }, rootGetters) => (teamId) => {
  if (!draft || !draft.picks) {
    return [];
  }

  const localTeamPicks = [];

  draft.picks.forEach(({ p, t, k }, i) => {
    if (p && t === teamId) {
      const player = lists.players[p];
      const team = rootGetters['lists/teamByAbbreviation'](player.team);

      player.pick = i + 1;
      player.id = p;
      player.fullTeam = `${team.city} ${team.mascot}`;
      player.keeper = k;
      localTeamPicks.push(player);
    }
  });

  return localTeamPicks;
};

const roster = (state, getters) => (teamId) => {
  const rosterTeamPicks = getters.teamPicks(teamId);
  const flexPositions = ['RB', 'WR'];
  const bench = [];
  const starters = getters.draftablePositions.map(({ position, starters: numStarters }) => {
    return {
      position,
      numStarters,
      players: [],
    };
  });

  rosterTeamPicks.sort((a, b) => {
    return a.rank - b.rank;
  });

  rosterTeamPicks.forEach((teamPick) => {
    const i = starters.findIndex(({ position }) => position === teamPick.position);
    const flexIndex = starters.findIndex(({ position }) => position === 'FLEX');
    const pick = {
      id: teamPick.id,
      name: teamPick.name,
      pick: teamPick.pick,
      position: teamPick.position,
      team: teamPick.fullTeam,
      round: getters.roundFromPick(teamPick.pick),
      keeper: teamPick.keeper,
    };

    const positionHasSpotOpen = starters[i].players.length < starters[i].numStarters;

    const draftHasFlex = flexIndex > -1;
    const isFlexPosition = flexPositions.includes(pick.position);
    const flexHasSpotOpen = draftHasFlex &&
        starters[flexIndex].players.length < starters[flexIndex].numStarters;

    if (positionHasSpotOpen) {
      starters[i].players.push(pick);
    } else if (draftHasFlex && isFlexPosition && flexHasSpotOpen) {
      starters[flexIndex].players.push(pick);
    } else {
      bench.push(pick);
    }
  });

  return {
    starters,
    bench,
  };
};

const draftingTeam = ({ draft }, getters) => {
  if (!draft || !draft.picks) {
    return null;
  }

  if (getters.isDraftComplete) {
    return null;
  }

  const teamId = draft.picks[draft.currentPick - 1].t;
  const team = draft.teams.find(({ id }) => id === teamId);

  return team;
};

const draftRosters = ({ draft }, getters) => {
  if (!draft) {
    return [];
  }

  return draft.teams.map(({ name, id }) => {
    return {
      id,
      name,
      picks: getters.teamPicks(id),
    };
  });
};

const lastTeamToPick = ({ draft }, getters) => {
  if (!draft || draft.lastDrafted === null || !draft.picks) {
    return null;
  }

  const lastPick = draft.picks.find(({ p }) => p === draft.lastDrafted);

  if (!lastPick) {
    return null;
  }

  const teamId = lastPick.t;
  const team = draft.teams.find(({ id }) => id === teamId);

  team.picks = getters.teamPicks(teamId)
    .sort((a, b) => {
      if (!a.keeper && !b.keeper) {
        return a.pick > b.pick;
      }

      if (a.keeper === b.keeper) {
        return 0;
      } else if (a.keeper) {
        return -1;
      }

      return 1;
    });

  return team;
};

const lastDraftedPlayer = ({ draft }, getters, { lists: { players } }, rootGetters) => {
  if (!draft || !draft.lastDrafted) {
    return null;
  }

  const { name, position, team } = players[draft.lastDrafted];
  const { primaryHexColor, abbreviation } = rootGetters['lists/teamByAbbreviation'](team);

  return {
    id: draft.lastDrafted,
    name,
    position,
    primaryHexColor,
    abbreviation,
  };
};

const mostRecentlyDraftedPlayer = ({ draft }, getters, { lists: { players } }, rootGetters) => {
  if (!draft || draft.lastDrafted === null || !draft.picks) {
    return null;
  }

  const lastPick = draft.picks.find(({ p }) => p === draft.lastDrafted);

  if (!lastPick || !lastPick.p) {
    return null;
  }

  const playerId = lastPick.p;

  const { name, position, team } = players[playerId];
  const { primaryHexColor, abbreviation } = rootGetters['lists/teamByAbbreviation'](team);

  return {
    id: playerId,
    name,
    position,
    primaryHexColor,
    abbreviation,
  };
};

const roundPickDisplay = ({ draft }, getters) => {
  if (!draft) {
    return '';
  }

  return `ROUND ${getters.currentRound} - PICK ${draft.currentPick}`;
};

const previousPickForDraftingTeam = ({ draft }, getters, { lists: { players } }) => {
  if (!draft || !draft.picks || !draft.picks[draft.currentPick - 1]) {
    return null;
  }

  let previousPick = null;

  for (let i = draft.currentPick; i >= 0; i -= 1) {
    const { p, t } = draft.picks[i];

    if (p && t === getters.draftingTeam.id) {
      const player = players[p];

      previousPick = `${player.position} ${player.name}`;
      break;
    }
  }

  return previousPick;
};

const picksAwayForDraftingTeam = ({ draft }, getters) => {
  if (!draft || !draft.picks) {
    return 0;
  }

  const next = draft.picks
    .findIndex(({ p, t }, i) => {
      return !p && t === getters.draftingTeam.id && draft.currentPick !== i + 1;
    });

  if (next > -1) {
    return (next + 1) - draft.currentPick;
  }

  return null;
};

const draftablePositions = ({ draft }) => {
  if (!draft) {
    return [];
  }

  return draft.positions.filter(({ starters }) => {
    return starters > 0;
  });
};

const keepersByTeam = ({ draft }, getters, { lists: { players } }) => {
  if (!draft) {
    return [];
  }

  return draft.teams
    .map(({ name, id, uid }) => {
      const keepers = [];

      if (draft.keepers && draft.keepers[id]) {
        draft.keepers[id].forEach(({ p, r }) => {
          const player = players[p];

          player.round = r;
          player.id = p;
          keepers.push(player);
        });
      }

      return {
        id,
        uid,
        name,
        keepers,
      };
    });
};

const tradeKeeperRoundOptions = ({ draft }, getters) => ({ id }, keeperMode = false) => {
  if (!draft || !draft.picks) {
    return [];
  }

  return draft.picks
    .map(({ t }, i) => {
      return {
        value: getters.roundFromPick(i + 1),
        label: getters.roundFromPick(i + 1),
        t,
      };
    })
    .filter(({ t, value }) => {
      if (t !== id) {
        return false;
      }

      const hasKeeperRound = draft.keepers && draft.keepers[id] && !keeperMode &&
        draft.keepers[id].find(({ r }) => r && value === r);

      const hasTradeRound = draft.trades && draft.trades.find((trade) => {
        if (trade.t1 === id || trade.t2 === id) {
          return trade.r1 === value || trade.r2 === value;
        }

        return false;
      });

      return !hasKeeperRound && !hasTradeRound;
    });
};

export default localGetters;
